/**
 * (c) 2021, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import {
  Box,
  Button,
  Grid,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
  VStack,
} from "@microbit/ui";
import { uiMessage } from "@microbit/ui/messages";
import {
  KnownLanguageId,
  languageFromId,
  languageOrder,
  uiPatternsMessage,
} from "@microbit/ui-patterns";
import { RefObject, useCallback, useId } from "react";
import { FormattedMessage } from "react-intl";
import { languagesForDialog, useSettings } from "./settings";

interface LanguageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  finalFocusRef: RefObject<HTMLButtonElement>;
}

/**
 * Sprachauswahl.
 *
 * Aufgebaut wie der LanguageDialog aus @microbit/ui-patterns - Kacheln mit dem
 * Namen der Sprache in ihr selbst und darunter auf Englisch -, aber mit
 * ausgegrauten Kacheln fuer Sprachen, in die die Lernumgebung nicht
 * uebersetzt ist. Der gemeinsame Dialog kennt keine nicht waehlbaren
 * Eintraege.
 */
export const LanguageDialog = ({
  isOpen,
  onClose,
  finalFocusRef,
}: LanguageDialogProps) => {
  const [settings, setSettings] = useSettings();
  const noteId = useId();
  const handleSelect = useCallback(
    (languageId: string) => {
      setSettings({ ...settings, languageId });
      onClose();
    },
    [settings, setSettings, onClose]
  );
  const ordered = [...languagesForDialog].sort(
    (a, b) =>
      Number(b.selectable) - Number(a.selectable) ||
      languageOrder(a.id) - languageOrder(b.id)
  );
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "full", md: "4xl" }}
      finalFocusRef={finalFocusRef}
    >
      <ModalHeader css={{ fontSize: "lg", fontWeight: "bold" }}>
        <FormattedMessage {...uiPatternsMessage("ui-patterns.language")} />
      </ModalHeader>
      <ModalBody>
        <VStack gap={3} width="100%">
          <Grid
            width="100%"
            columns={{ base: 1, sm: 2, md: 3 }}
            gap={4}
            data-testid="language-grid"
          >
            {ordered.map((language) => (
              <LanguageCard
                key={language.id}
                id={language.id}
                selectable={language.selectable}
                current={settings.languageId === language.id}
                noteId={noteId}
                onSelect={handleSelect}
              />
            ))}
          </Grid>
          <Text id={noteId} fontSize="xs" alignSelf="flex-start" mt={1}>
            <FormattedMessage id="language-unavailable-note" />
          </Text>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" onPress={onClose} css={{ ms: "auto" }}>
          <FormattedMessage {...uiMessage("ui.close-action")} />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

interface LanguageCardProps {
  id: KnownLanguageId;
  selectable: boolean;
  current: boolean;
  noteId: string;
  onSelect: (languageId: string) => void;
}

const LanguageCard = ({
  id,
  selectable,
  current,
  noteId,
  onSelect,
}: LanguageCardProps) => {
  const { name, enName } = languageFromId(id);
  const nameId = useId();
  const enNameId = useId();
  return (
    <Box position="relative" w="100%" css={{ opacity: selectable ? 1 : 0.4 }}>
      <Button
        variant="plain"
        aria-labelledby={`${nameId} ${enNameId}`}
        aria-describedby={selectable ? undefined : noteId}
        aria-current={current ? "true" : undefined}
        isDisabled={!selectable}
        onPress={() => onSelect(id)}
        data-testid={id}
        css={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          borderRadius: "xl",
          borderWidth: "2px",
          borderColor: current ? "brand.500" : "gray.200",
          cursor: selectable ? "pointer" : "not-allowed",
          _hover: selectable ? { bg: "gray.100" } : {},
        }}
      />
      <VStack
        alignItems="flex-start"
        w="100%"
        css={{
          position: "relative",
          pointerEvents: "none",
          py: 4,
          px: 5,
          color: selectable ? "languageText" : "gray.600",
        }}
      >
        <Text
          id={nameId}
          as="span"
          lang={id}
          dir="auto"
          fontSize="xl"
          fontWeight="semibold"
        >
          {name}
        </Text>
        <Text
          id={enNameId}
          as="span"
          lang="en"
          fontWeight="normal"
          fontSize="sm"
          color="gray.700"
        >
          {enName}
        </Text>
      </VStack>
    </Box>
  );
};
